import React, { useState, useEffect } from 'react';
import { theme } from '@weave/theme-original';
import {
  Chip,
  Heading,
  Text,
  PrimaryButton,
  SecondaryButton,
  DropdownField,
  useFormField,
  TextLink,
  useModalControl,
  Modal,
  IconButton,
  TrashIcon,
} from '@weave/design-system';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import {
  useAnnouncementUpsertMutation,
  useSimpleAnnouncementDeleteMutation,
  useSimpleAnnouncementPublishMutation,
} from './use-announcement-mutations';
import { AnnouncementData, SimpleAnnouncementData } from './types';
import { AnnouncementForm } from './announcement-form';
import { getName } from './utils';
import { parseInlineMarkdown } from '../../utils/parseInlineMarkdown';

// Extend dayjs with plugins
dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(localizedFormat);

// Helper function to format timestamps to human-readable format
const formatTimestamp = (timestamp: string): string => {
  return dayjs(timestamp).format('MMM D, YYYY h:mm:ss A z');
};

const DetailItem = ({
  label,
  value,
}: {
  label: string;
  value: string | React.ReactNode;
}) => {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '180px 1fr',
        gap: theme.spacing(2),
      }}
    >
      <Text as="div" weight="bold">
        {label}
      </Text>
      <Text as="div">{value}</Text>
    </div>
  );
};

export const AnnouncementDetail = ({
  announcement,
}: {
  announcement: AnnouncementData;
}) => {
  const allVersions = announcement.versions || [];
  const [selectedVersion, setSelectedVersion] = useState(() => {
    if (allVersions.length > 0) {
      // Use activeVersionId from announcement data
      return announcement.activeVersionId;
    }
    return 1;
  });

  const { mutate: upsertAnnouncement } = useAnnouncementUpsertMutation();

  const updatePublishStatus = (isPublished: boolean) => {
    upsertAnnouncement({
      data: announcement,
      update: { isPublished },
      mode: 'update',
    });
  };

  const updateActiveVersion = (version: number) => {
    // Simply update the activeVersionId
    upsertAnnouncement({
      data: announcement,
      update: { activeVersionId: version },
      mode: 'update',
    });
  };

  // Update selected version when announcement changes
  useEffect(() => {
    if (allVersions.length > 0) {
      setSelectedVersion(announcement.activeVersionId);
    }
  }, [announcement.activeVersionId, allVersions.length]);

  const selectedVersionData =
    allVersions.find((v) => v.version === selectedVersion) || allVersions[0];

  if (!selectedVersionData) {
    return <div>No announcement selected</div>;
  }

  const [isEditing, setIsEditing] = useState(false);

  // Use useFormField for the version selector
  const versionSelectorProps = useFormField({
    type: 'dropdown',
    value: selectedVersion.toString(),
  });

  return (
    <div
      style={{
        background: theme.colors.white,
        padding: theme.spacing(3),
        borderRadius: theme.borderRadius.medium,
        border: `1px solid ${theme.colors.gray300}`,
        display: 'grid',
        gap: theme.spacing(3),
        gridTemplateRows: 'auto 1fr',
        height: '100%',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          alignItems: 'center',
          gap: theme.spacing(2),
        }}
      >
        <Heading css={{ margin: 0 }} level={3}>
          Announcement Details
        </Heading>

        {!isEditing && (
          <div
            style={{
              display: 'grid',
              gridAutoFlow: 'column',
              gap: theme.spacing(1),
            }}
          >
            <PrimaryButton
              destructive={announcement.isPublished}
              onClick={() => {
                setIsEditing(false);
                updatePublishStatus(!announcement.isPublished);
              }}
            >
              {announcement.isPublished ? 'Unpublish' : 'Publish'}
            </PrimaryButton>
          </div>
        )}
      </div>

      <>
        {isEditing ? (
          <AnnouncementForm
            mode="edit"
            announcementData={announcement}
            versionData={selectedVersionData}
            onCancel={() => setIsEditing(false)}
            onSave={({ version }) => {
              console.log('Saving announcement revision:', { announcement, version });
              setIsEditing(false);
              upsertAnnouncement(
                {
                  data: announcement,
                  update: {
                    versions: [...announcement.versions, version],
                    activeVersionId: version.version,
                  },
                  mode: 'update',
                },
                {
                  onSuccess: () => {
                    setSelectedVersion(version.version);
                  },
                }
              );
            }}
          />
        ) : (
          <div
            style={{
              display: 'grid',
              gap: theme.spacing(1),
              gridAutoRows: 'max-content',
            }}
          >
            <DetailItem label="ID" value={announcement.id} />
            <DetailItem
              label="Publish Status"
              value={
                <div style={{ marginTop: theme.spacing(1) }}>
                  {announcement.isPublished ? (
                    <Chip variant="success">Published</Chip>
                  ) : (
                    <Chip variant="warn">Unpublished</Chip>
                  )}
                </div>
              }
            />

            <Text as="div" weight="bold">
              Version
            </Text>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto max-content',
                gridTemplateAreas: '"select controls"',
              }}
            >
              {allVersions.length > 1 && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing(1),
                    gridArea: 'select',
                  }}
                >
                  <DropdownField
                    {...versionSelectorProps}
                    id="version-selector"
                    name="version-selector"
                    label=""
                    value={selectedVersion.toString()}
                    onChange={(e) => setSelectedVersion(parseInt(e.value))}
                    css={{ minWidth: '160px' }}
                  >
                    {allVersions
                      .sort((a, b) => b.version - a.version)
                      .map((version) => (
                        <DropdownField.Option
                          key={version.version}
                          value={version.version.toString()}
                        >
                          Version {version.version}{' '}
                          {version.version === announcement.activeVersionId && '(Active)'}
                        </DropdownField.Option>
                      ))}
                  </DropdownField>
                </div>
              )}
              <div
                style={{
                  display: 'grid',
                  gridAutoFlow: 'column',
                  gap: theme.spacing(1),
                  gridArea: 'controls',
                }}
              >
                <SecondaryButton onClick={() => setIsEditing(true)}>
                  Create Revision
                </SecondaryButton>
                {selectedVersion !== announcement.activeVersionId && (
                  <PrimaryButton
                    onClick={() => {
                      updateActiveVersion(selectedVersion);
                    }}
                  >
                    Activate
                  </PrimaryButton>
                )}
              </div>
            </div>
            <div
              style={{
                border: `1px solid ${theme.colors.gray300}`,
                borderRadius: theme.borderRadius.small,
                padding: theme.spacing(2),
                display: 'grid',
                gap: theme.spacing(1),
                gridAutoRows: 'max-content',
              }}
            >
              <DetailItem label="Version" value={`v${selectedVersionData.version}`} />
              <DetailItem label="Title" value={selectedVersionData.title} />
              <DetailItem label="Type" value={selectedVersionData.type} />
              <DetailItem
                label="Version Status"
                value={
                  <div style={{ marginTop: theme.spacing(1) }}>
                    {selectedVersion === announcement.activeVersionId ? (
                      <Chip variant="success">Active</Chip>
                    ) : (
                      <Chip variant="warn">Inactive</Chip>
                    )}
                  </div>
                }
              />
              <DetailItem label="Priority" value={selectedVersionData.priority} />
              <DetailItem label="Path" value={selectedVersionData.path} />
              <DetailItem
                label="Message"
                value={parseInlineMarkdown(selectedVersionData.message)}
              />
              <DetailItem
                label="Action"
                value={
                  selectedVersionData.action ? (
                    <TextLink
                      href={selectedVersionData.action.href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {selectedVersionData.action.label}
                    </TextLink>
                  ) : (
                    'N/A'
                  )
                }
              />
              <DetailItem label="Created by" value={selectedVersionData.createdBy} />
              <DetailItem
                label="Created On"
                value={formatTimestamp(selectedVersionData.createdOn)}
              />
            </div>
          </div>
        )}
      </>
    </div>
  );
};

export const SimpleAnnouncementDetail = ({
  announcement,
}: {
  announcement: SimpleAnnouncementData;
}) => {
  const { modalProps, triggerProps } = useModalControl();
  const { mutate: upsertAnnouncement } = useSimpleAnnouncementPublishMutation();
  const { mutate: deleteAnnouncement } = useSimpleAnnouncementDeleteMutation();

  const updatePublishStatus = (isPublished: boolean) => {
    upsertAnnouncement({
      announcementId: announcement.id,
      setPublished: isPublished,
    });
  };

  const handleDeleteAnnouncement = () => {
    deleteAnnouncement(announcement.id);
  };

  if (!announcement) {
    return <div>No announcement selected</div>;
  }

  const [isEditing, setIsEditing] = useState(false);

  return (
    <div
      style={{
        background: theme.colors.white,
        padding: theme.spacing(3),
        borderRadius: theme.borderRadius.medium,
        border: `1px solid ${theme.colors.gray300}`,
        display: 'grid',
        gap: theme.spacing(3),
        gridTemplateRows: 'auto 1fr',
        height: '100%',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          alignItems: 'center',
          gap: theme.spacing(2),
        }}
      >
        <Heading css={{ margin: 0 }} level={3}>
          Announcement Details
        </Heading>

        {!isEditing && (
          <div
            style={{
              display: 'grid',
              gridAutoFlow: 'column',
              gap: theme.spacing(1),
            }}
          >
            <PrimaryButton
              destructive={announcement.isPublished}
              onClick={() => {
                setIsEditing(false);
                updatePublishStatus(!announcement.isPublished);
              }}
            >
              {announcement.isPublished ? 'Unpublish' : 'Publish'}
            </PrimaryButton>
            <IconButton destructive label="Delete" onClick={handleDeleteAnnouncement}>
              <TrashIcon />
            </IconButton>
          </div>
        )}
      </div>

      <div
        style={{
          display: 'grid',
          gap: theme.spacing(1),
          gridAutoRows: 'max-content',
        }}
      >
        {' '}
        <div
          style={{
            border: `1px solid ${theme.colors.gray300}`,
            borderRadius: theme.borderRadius.small,
            padding: theme.spacing(2),
            display: 'grid',
            gap: theme.spacing(1),
            gridAutoRows: 'max-content',
          }}
        >
          {/* <DetailItem label="ID" value={announcement.id} /> */}
          <DetailItem label="Title" value={announcement.title} />
          <DetailItem
            label="Status"
            value={
              <div>
                {announcement.isPublished ? (
                  <Chip variant="success">Published</Chip>
                ) : (
                  <Chip variant="warn">Unpublished</Chip>
                )}
              </div>
            }
          />

          <DetailItem label="Type" value={announcement.type} />
          <DetailItem label="Priority" value={announcement.priority} />
          <DetailItem label="Path" value={announcement.path} />
          <DetailItem
            label="Action"
            value={
              announcement.action ? (
                <TextLink
                  href={announcement.action.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {announcement.action.label}
                </TextLink>
              ) : (
                'N/A'
              )
            }
          />
          <DetailItem label="Created by" value={announcement.createdBy} />
          <DetailItem
            label="Created On"
            value={formatTimestamp(announcement.createdOn)}
          />
          {announcement.history.length > 0 && (
            <DetailItem
              label="Last Update"
              value={
                <div style={{ display: 'grid', gap: theme.spacing(1) }}>
                  <Text as="div">
                    {announcement.history[announcement.history.length - 1].actionType} by{' '}
                    {announcement.history[announcement.history.length - 1].userInfo
                      ? getName(
                          announcement.history[announcement.history.length - 1].userInfo!
                        )
                      : 'Unknown User'}
                  </Text>
                  <SecondaryButton {...triggerProps}>View History</SecondaryButton>
                  <Modal {...modalProps} maxWidth={600}>
                    <Modal.Header onClose={modalProps.onClose}>
                      Announcement History
                    </Modal.Header>
                    <Modal.Body css={{ display: 'grid', gap: theme.spacing(2) }}>
                      <table>
                        <thead>
                          <tr>
                            <th
                              style={{
                                textAlign: 'left',
                                padding: theme.spacing(1),
                                borderBottom: `1px solid ${theme.colors.gray300}`,
                              }}
                            >
                              Action
                            </th>
                            <th
                              style={{
                                textAlign: 'left',
                                padding: theme.spacing(1),
                                borderBottom: `1px solid ${theme.colors.gray300}`,
                              }}
                            >
                              User ID
                            </th>
                            <th
                              style={{
                                textAlign: 'left',
                                padding: theme.spacing(1),
                                borderBottom: `1px solid ${theme.colors.gray300}`,
                              }}
                            >
                              Timestamp
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {announcement.history.map((entry, index) => (
                            <tr key={index}>
                              <td
                                style={{
                                  padding: theme.spacing(1),
                                  borderBottom: `1px solid ${theme.colors.gray300}`,
                                }}
                              >
                                {entry.actionType}
                              </td>
                              <td
                                style={{
                                  padding: theme.spacing(1),
                                  borderBottom: `1px solid ${theme.colors.gray300}`,
                                }}
                              >
                                {entry.userInfo
                                  ? getName(entry.userInfo)
                                  : 'Unknown User'}
                              </td>
                              <td
                                style={{
                                  padding: theme.spacing(1),
                                  borderBottom: `1px solid ${theme.colors.gray300}`,
                                }}
                              >
                                {entry.eventTime ? formatTimestamp(entry.eventTime) : ''}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Modal.Body>
                  </Modal>
                </div>
              }
            />
          )}
        </div>
        <Text as="div" weight="bold">
          Message
        </Text>
        <Text as="div">{parseInlineMarkdown(announcement.message)}</Text>
      </div>
    </div>
  );
};
